import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CameraswitchOutlined } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { uploadFileToPresignedUrl } from '../services/familyApi';
import {
  completeLivePhotoUpload,
  createLivePhotoUpload,
  getLivePhotoLinkStatus,
} from '../services/livePhotoApi';

function cleanupStream(stream) {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
}

function getLivePhotoAspectRatio(status) {
  const width = Number(status?.targetWidthPixels);
  const height = Number(status?.targetHeightPixels);

  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return width / height;
  }

  return 3.5 / 4.5;
}

function getLivePhotoTargetDimensions(status) {
  const width = Number(status?.targetWidthPixels);
  const height = Number(status?.targetHeightPixels);

  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  }

  return {
    width: 414,
    height: 532,
  };
}

function LivePhotoCapturePage() {
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [capturedFile, setCapturedFile] = useState(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState('');
  const sessionId = useMemo(() => searchParams.get('sessionId') ?? '', [searchParams]);
  const livePhotoAspectRatio = useMemo(() => getLivePhotoAspectRatio(status), [status]);
  const livePhotoTargetDimensions = useMemo(() => getLivePhotoTargetDimensions(status), [status]);

  useEffect(() => {
    let active = true;

    async function loadStatus() {
      if (!sessionId) {
        setError('This live photo link is missing its session ID.');
        setLoading(false);
        return;
      }

      try {
        const response = await getLivePhotoLinkStatus({ sessionId });
        if (!active) {
          return;
        }

        setStatus(response);
        setError('');
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError(loadError.message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadStatus();

    return () => {
      active = false;
    };
  }, [sessionId]);

  useEffect(() => {
    return () => {
      cleanupStream(streamRef.current);
      if (capturedPreviewUrl) {
        URL.revokeObjectURL(capturedPreviewUrl);
      }
    };
  }, [capturedPreviewUrl]);

  useEffect(() => {
    if (loading || !status || status.isExpired || capturedFile || cameraStream) {
      return undefined;
    }

    let active = true;

    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Camera capture is not supported in this browser.');
        return;
      }

      try {
        setCameraError('');
        setCameraReady(false);

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: cameraFacingMode,
            width: { ideal: 1600 },
            height: { ideal: 1600 },
          },
          audio: false,
        });

        if (!active) {
          cleanupStream(stream);
          return;
        }

        cleanupStream(streamRef.current);
        streamRef.current = stream;
        setCameraStream(stream);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (streamError) {
        if (active) {
          setCameraError(streamError.message ?? 'We could not access your camera.');
        }
      }
    }

    startCamera();

    return () => {
      active = false;
    };
  }, [cameraFacingMode, cameraStream, capturedFile, loading, status]);

  function handleFlipCamera() {
    if (cameraBusy) {
      return;
    }

    cleanupStream(streamRef.current);
    streamRef.current = null;
    setCameraStream(null);
    setCameraReady(false);
    setCameraError('');
    setCameraFacingMode((currentFacingMode) => (currentFacingMode === 'user' ? 'environment' : 'user'));
  }

  async function handleCapturePhoto() {
    if (!videoRef.current) {
      setCameraError('Camera preview is not ready yet.');
      return;
    }

    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      setCameraError('Camera preview is not ready yet. Try again in a moment.');
      return;
    }

    try {
      const targetAspectRatio = livePhotoAspectRatio;
      const { width: targetWidth, height: targetHeight } = livePhotoTargetDimensions;
      let cropWidth = video.videoWidth;
      let cropHeight = Math.round(cropWidth / targetAspectRatio);

      if (cropHeight > video.videoHeight) {
        cropHeight = video.videoHeight;
        cropWidth = Math.round(cropHeight * targetAspectRatio);
      }

      const offsetX = Math.max(0, Math.round((video.videoWidth - cropWidth) / 2));
      const offsetY = Math.max(0, Math.round((video.videoHeight - cropHeight) / 2));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to prepare the captured photo.');
      }

      context.drawImage(video, offsetX, offsetY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (value) => {
            if (value) {
              resolve(value);
              return;
            }

            reject(new Error('Unable to capture a photo from the camera.'));
          },
          'image/jpeg',
          0.95
        );
      });

      const file = new File([blob], `live-photo-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`, {
        type: 'image/jpeg',
      });

      if (capturedPreviewUrl) {
        URL.revokeObjectURL(capturedPreviewUrl);
      }

      setCapturedFile(file);
      setCapturedPreviewUrl(URL.createObjectURL(file));
      cleanupStream(streamRef.current);
      streamRef.current = null;
      setCameraStream(null);
      setCameraReady(false);
      setCameraError('');
    } catch (captureError) {
      setCameraError(captureError.message ?? 'Unable to capture a photo right now.');
    }
  }

  function handleRetry() {
    if (capturedPreviewUrl) {
      URL.revokeObjectURL(capturedPreviewUrl);
    }

    setCapturedFile(null);
    setCapturedPreviewUrl('');
    setCameraError('');
    setCameraReady(false);
  }

  async function handleSendPhoto() {
    if (!capturedFile || !sessionId) {
      return;
    }

    setCameraBusy(true);
    setCameraError('');

    try {
      const upload = await createLivePhotoUpload({
        sessionId,
        fileName: capturedFile.name,
        contentType: capturedFile.type || 'image/jpeg',
      });

      await uploadFileToPresignedUrl({
        uploadUrl: upload.uploadUrl,
        file: capturedFile,
        contentType: capturedFile.type || 'image/jpeg',
      });

      const updatedStatus = await completeLivePhotoUpload({
        sessionId,
        objectKey: upload.objectKey,
        fileName: capturedFile.name,
        contentType: capturedFile.type || 'image/jpeg',
      });

      setStatus(updatedStatus);
    } catch (uploadError) {
      setCameraError(uploadError.message ?? 'Unable to send this photo right now.');
    } finally {
      setCameraBusy(false);
    }
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        background: 'linear-gradient(180deg, #f6f8fb 0%, #edf3ff 100%)',
        display: 'flex',
        minHeight: '100vh',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontWeight: 700 }} variant="h4">
                Take Your Visa Photo
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                Capture a photo here and we’ll send it back to your desktop session.
              </Typography>
            </Box>

            {loading ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                <CircularProgress size={28} />
                <Typography variant="body2">Checking this live photo link...</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : status?.isExpired ? (
              <Alert severity="warning">This live photo link has expired. Start a new one from your computer.</Alert>
            ) : (
              <>
                {status?.hasUploadedPhoto ? (
                  <Alert severity="success">
                    Photo uploaded. You can retake it here and your desktop will keep receiving the latest version for
                    this session until it expires.
                  </Alert>
                ) : null}

                {cameraError ? <Alert severity="error">{cameraError}</Alert> : null}

                {capturedPreviewUrl ? (
                  <Stack spacing={2}>
                    <Typography color="text.secondary" variant="body2">
                      Review the exact exported photo, then send it to your desktop session.
                    </Typography>
                    <Box
                      sx={{
                        aspectRatio: `${livePhotoAspectRatio}`,
                        bgcolor: '#111',
                        borderRadius: 3,
                        mx: 'auto',
                        width: '100%',
                        maxWidth: 420,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="img"
                        src={capturedPreviewUrl}
                        alt="Captured live photo preview"
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          border: '2px solid rgba(255,255,255,0.92)',
                          borderRadius: 3,
                          inset: 0,
                          pointerEvents: 'none',
                          position: 'absolute',
                          zIndex: 1,
                        }}
                      />
                    </Box>
                    <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="caption">
                      Exported at exactly {livePhotoTargetDimensions.width}x{livePhotoTargetDimensions.height}px.
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      <Button disabled={cameraBusy} fullWidth onClick={handleRetry}>
                        Retry
                      </Button>
                      <Button disabled={cameraBusy} fullWidth onClick={handleSendPhoto} variant="contained">
                        {cameraBusy ? 'Sending...' : 'Send To Desktop'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography color="text.secondary" variant="body2">
                      Use even lighting, face the camera directly, and keep a light background if possible.
                    </Typography>
                    <Box
                      sx={{
                        aspectRatio: `${livePhotoAspectRatio}`,
                        bgcolor: '#111',
                        borderRadius: 3,
                        mx: 'auto',
                        width: '100%',
                        maxWidth: 420,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {!cameraReady ? (
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                          sx={{
                            color: 'rgba(255,255,255,0.9)',
                            inset: 0,
                            position: 'absolute',
                            zIndex: 1,
                          }}
                        >
                          <CircularProgress color="inherit" size={22} />
                          <Typography color="inherit" variant="body2">
                            Starting camera preview...
                          </Typography>
                        </Stack>
                      ) : null}
                      <video
                        autoPlay
                        muted
                        onCanPlay={() => setCameraReady(true)}
                        onLoadedData={() => setCameraReady(true)}
                        playsInline
                        ref={videoRef}
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: cameraFacingMode === 'user' ? 'scaleX(-1)' : 'none',
                        }}
                      />
                      <Tooltip title={`Use ${cameraFacingMode === 'user' ? 'back' : 'front'} camera`}>
                        <span>
                          <IconButton
                            aria-label={`Use ${cameraFacingMode === 'user' ? 'back' : 'front'} camera`}
                            disabled={cameraBusy}
                            onClick={handleFlipCamera}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(0,0,0,0.56)',
                              color: '#fff',
                              position: 'absolute',
                              right: 12,
                              top: 12,
                              zIndex: 3,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.72)' },
                            }}
                          >
                            <CameraswitchOutlined fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Box
                        sx={{
                          border: '2px solid rgba(255,255,255,0.92)',
                          borderRadius: 3,
                          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.32)',
                          inset: 0,
                          pointerEvents: 'none',
                          position: 'absolute',
                          zIndex: 2,
                        }}
                      />
                    </Box>
                    <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="caption">
                      The captured photo will be exported at exactly {livePhotoTargetDimensions.width}x
                      {livePhotoTargetDimensions.height}px from this frame.
                    </Typography>
                    <Button disabled={!cameraStream || !cameraReady} onClick={handleCapturePhoto} variant="contained">
                      Capture Photo
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default LivePhotoCapturePage;
